import React from "react";
import { FiBell, FiShield, FiSliders, FiUser, FiUsers } from "react-icons/fi";
import TelegramIdLogin from "../components/TelegramIdLogin";
import { useUserSession } from "../auth/UserSessionContext";
import { pendingReferralCode } from "../referrals/referralLink";
import ProfileOverview from "./ProfileOverview";
import ProfileNotifications from "./ProfileNotifications";
import ProfilePreferences from "./ProfilePreferences";
import ReferralProgram from "./ReferralProgram";
import "./Profile.css";
import "./ProfileLayout.css";

export default function ProfileWorkspace({ onOpenSectionOrder, adminPanel }) {
  const session = useUserSession();
  const [section, setSection] = React.useState("overview");
  const [adminVisited, setAdminVisited] = React.useState(false);
  const { status, requestTransientProfile, clearTransientProfile } = session;
  const isAdmin = session.session?.roles?.includes("ADMIN") === true;
  const selectSection = (nextSection) => {
    if (nextSection === "admin") setAdminVisited(true);
    setSection(nextSection);
  };

  React.useEffect(() => {
    if (status === "authenticated") requestTransientProfile();
    return clearTransientProfile;
  }, [status, requestTransientProfile, clearTransientProfile]);

  React.useEffect(() => {
    if (!isAdmin && section === "admin") setSection("overview");
  }, [isAdmin, section]);

  if (session.status !== "authenticated") return (
    <main className="profile-workspace profile-public">
      <h1>Your Cryptember profile</h1>
      <p>Manage notifications, preferences, referrals and manual USDT payouts in one secure workspace.</p>
      <p>Telegram verifies your account. Insights keeps a secure cookie and does not store your Telegram ID in the browser.</p>
      {pendingReferralCode() && <p className="profile-referral-context">You followed a Cryptember referral link. It will be applied after sign-in.</p>}
      <TelegramIdLogin onTelegramPayload={({ mode, fields }) => session.login(fields, mode)} />
      {session.status === "authenticating" && <p role="status">Waiting for App backend identity verification…</p>}
      {session.error && <p role="alert">Sign-in is temporarily unavailable. Please try again.</p>}
    </main>
  );

  const items = [
    ["overview", "Overview", FiUser],
    ["notifications", "Notifications", FiBell],
    ["referrals", "Referral Program", FiUsers],
    ["preferences", "Preferences", FiSliders],
    ...(isAdmin ? [["admin", "Admin", FiShield]] : []),
  ];

  return (
    <main className="profile-workspace">
      <div className="profile-heading">
        <div><h1>Profile</h1><p>Account and referral controls</p></div>
        <button type="button" onClick={session.logout}>Log out</button>
      </div>
      <div className="profile-layout">
        <nav className="profile-rail" aria-label="Profile sections">
          {items.map(([id, label, Icon]) => (
            <button type="button" key={id} className={section === id ? "active" : ""}
              aria-label={label} aria-current={section === id ? "page" : undefined}
              onClick={() => selectSection(id)}>
              <Icon size={22} aria-hidden="true" focusable="false" />
              <span className="profile-nav-label" aria-hidden="true">{label}</span>
            </button>
          ))}
        </nav>
        <div className="profile-content">
          {section === "overview" && <ProfileOverview profile={session.transientProfile} snapshot={session.snapshot} />}
          {section === "notifications" && <ProfileNotifications
            notifications={session.snapshot?.settings?.eventNotifications}
            submitCommand={session.submitCommand} revision={session.snapshot?.revision} />}
          {section === "referrals" && <ReferralProgram referral={session.snapshot?.referrals} submitCommand={session.submitCommand} />}
          {section === "preferences" && <ProfilePreferences
            preferences={session.snapshot?.settings?.preferences} submitCommand={session.submitCommand}
            revision={session.snapshot?.revision} onOpenSectionOrder={onOpenSectionOrder} />}
          {isAdmin && adminVisited && (
            <div style={{ display: section === "admin" ? "block" : "none" }}>
              {adminPanel}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
